import { ref } from 'vue';
import { defineStore } from 'pinia';

import type {
  CarrierName,
  CarrierStaticInfo,
  IngredientId,
  OverviewActivityStaticInfo,
  OverviewDestinationStaticInfo,
  OverviewHeartedStaticInfo,
  OverviewStaticInfoResult,
  PortStaticInfo,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';
import { assertValue, getHotelKey, readonlyRef } from '@libs/utils';

export const useStaticInfoStore = defineStore('StaticInfoStore', () => {
  // State
  const carrierNames = ref<Record<string, CarrierName>>({});
  const airportStaticInfo = ref<Record<string, PortStaticInfo>>({});
  const seaportStaticInfo = ref<Record<string, PortStaticInfo>>({});
  const carrierStaticInfo = ref<Record<string, CarrierStaticInfo>>({});
  const heartedHotelsOverviewStaticInfo = ref<
    Record<string, OverviewHeartedStaticInfo>
  >({});
  const subIngredientsOverviewStaticInfo = ref<
    Record<string, OverviewActivityStaticInfo>
  >({});
  const destinationOverviewStaticInfo = ref<
    Record<string, OverviewDestinationStaticInfo>
  >({});
  const pendingDestinationKeys = new Set<string>();
  const pendingSubIngredientKeys = new Set<string>();

  // Getters
  function getOverviewStaticInfo(
    hotelCode: string,
    ingredientId: IngredientId
  ): OverviewDestinationStaticInfo | undefined {
    return destinationOverviewStaticInfo.value[
      getHotelKey(hotelCode, ingredientId)
    ];
  }

  function hasDestinationStaticInfo(
    hotelCode: string,
    ingredientId: IngredientId
  ): boolean {
    const key = getHotelKey(hotelCode, ingredientId);
    return (
      !!destinationOverviewStaticInfo.value[key] ||
      pendingDestinationKeys.has(key)
    );
  }

  function getSubIngredientStaticInfo(
    code: string,
    ingredientId: IngredientId
  ): OverviewActivityStaticInfo | undefined {
    return subIngredientsOverviewStaticInfo.value[
      getHotelKey(code, ingredientId)
    ];
  }

  function hasSubIngredientStaticInfo(
    code: string,
    ingredientId: IngredientId
  ): boolean {
    const key = getHotelKey(code, ingredientId);
    return (
      !!subIngredientsOverviewStaticInfo.value[key] ||
      pendingSubIngredientKeys.has(key)
    );
  }

  function getPortStaticInfo(
    code: string,
    kind: IngredientType
  ): PortStaticInfo | undefined {
    if (kind === IngredientType.Ferry) {
      return seaportStaticInfo.value[code];
    }

    if (kind === IngredientType.Flight) {
      return airportStaticInfo.value[code];
    }
  }

  function getPortsStaticInfo(
    ports: string[],
    kind: IngredientType
  ): Record<string, PortStaticInfo> {
    const portsStaticInfo: Record<string, PortStaticInfo> = {};
    if (kind === IngredientType.Flight) {
      ports.forEach((port) => {
        const airportInfo = airportStaticInfo.value[port];
        if (airportInfo) {
          portsStaticInfo[port] = airportInfo;
        }
      });
    }
    if (kind === IngredientType.Ferry) {
      ports.forEach((port) => {
        const seaportInfo = seaportStaticInfo.value[port];
        if (seaportInfo) {
          portsStaticInfo[port] = seaportInfo;
        }
      });
    }
    return portsStaticInfo;
  }

  function getCarriersStaticInfo(
    carriers: string[]
  ): Record<string, CarrierStaticInfo> {
    const result: Record<string, CarrierStaticInfo> = {};

    carriers.forEach((carrier) => {
      const carrierInfo = carrierStaticInfo.value[carrier];

      if (carrierInfo) {
        result[carrier] = carrierInfo;
      }
    });
    // FYI: This is a fallback for when the carrierStaticInfo is empty / server returns error.
    if (Object.keys(result).length === 0) {
      const fallbackResult: Record<string, CarrierStaticInfo> = {};

      carriers.forEach((carrier) => {
        fallbackResult[carrier] = {
          code: carrier,
          name: carrier,
          logo: '',
        };
      });

      insertCarrierStaticInfo(fallbackResult);

      return fallbackResult;
    }

    return result;
  }

  function getPortCodesWithStaticInfo(kind: IngredientType): string[] {
    if (kind === IngredientType.Ferry) {
      return Object.keys(seaportStaticInfo.value);
    }

    if (kind === IngredientType.Flight) {
      return Object.keys(airportStaticInfo.value);
    }

    return [];
  }

  function getCarrierCodesWithStaticInfo(): string[] {
    return Object.keys(carrierStaticInfo.value);
  }

  // Actions
  function markDestinationAsPending(
    hotelCode: string,
    ingredientId: IngredientId
  ): void {
    pendingDestinationKeys.add(getHotelKey(hotelCode, ingredientId));
  }

  function markSubIngredientAsPending(
    code: string,
    ingredientId: IngredientId
  ): void {
    pendingSubIngredientKeys.add(getHotelKey(code, ingredientId));
  }

  function clearPendingDestination(
    hotelCode: string,
    ingredientId: IngredientId
  ): void {
    pendingDestinationKeys.delete(getHotelKey(hotelCode, ingredientId));
  }

  function clearPendingSubIngredient(
    code: string,
    ingredientId: IngredientId
  ): void {
    pendingSubIngredientKeys.delete(getHotelKey(code, ingredientId));
  }

  function insertOverviewStaticInfo({
    destinationStaticInfo,
    subIngredientsStaticInfo,
    heartedHotelsStaticInfo,
  }: OverviewStaticInfoResult): void {
    for (const destinationKey in destinationStaticInfo) {
      const destinationValue = destinationStaticInfo[destinationKey];
      assertValue(
        destinationValue,
        'staticInfoStore.ts > insertOverviewStaticInfo > destinationValue'
      );

      const destinationCacheKey = getHotelKey(
        destinationValue.hotelCode,
        destinationValue.ingredientId
      );
      destinationOverviewStaticInfo.value[destinationCacheKey] ??=
        destinationValue;
      pendingDestinationKeys.delete(destinationCacheKey);
    }
    for (const subIngredientKey in subIngredientsStaticInfo) {
      const subIngredientValue = subIngredientsStaticInfo[subIngredientKey];
      assertValue(
        subIngredientValue,
        'staticInfoStore.ts > insertOverviewStaticInfo > subIngredientValue'
      );

      const subIngredientCacheKey = getHotelKey(
        subIngredientValue.hotelCode,
        subIngredientValue.ingredientId
      );
      subIngredientsOverviewStaticInfo.value[subIngredientCacheKey] ??=
        subIngredientValue;
      pendingSubIngredientKeys.delete(subIngredientCacheKey);
    }
    for (const heartedHotelKey in heartedHotelsStaticInfo) {
      const heartedHotelValue = heartedHotelsStaticInfo[heartedHotelKey];
      assertValue(
        heartedHotelValue,
        'staticInfoStore.ts > insertOverviewStaticInfo > heartedHotelValue'
      );

      const heartedHotelCacheKey = getHotelKey(
        heartedHotelValue.hotelCode,
        heartedHotelValue.ingredientId
      );
      heartedHotelsOverviewStaticInfo.value[heartedHotelCacheKey] ??=
        heartedHotelValue;
      pendingDestinationKeys.delete(heartedHotelCacheKey);
    }
  }

  function insertCarrierStaticInfo(
    newCarrierInfo: Record<string, CarrierStaticInfo>
  ): void {
    Object.assign(carrierStaticInfo.value, newCarrierInfo);
  }

  function insertPortStaticInfo(
    newPortStaticInfo: Record<string, PortStaticInfo>,
    kind: IngredientType
  ): void {
    if (kind === IngredientType.Ferry) {
      Object.assign(seaportStaticInfo.value, newPortStaticInfo);
    }

    if (kind === IngredientType.Flight) {
      Object.assign(airportStaticInfo.value, newPortStaticInfo);
    }
  }

  function insertCarrierNames(
    newCarrierNames: Record<string, CarrierName>
  ): void {
    Object.assign(carrierNames.value, newCarrierNames);
  }

  function setCarrierStaticInfo(
    newCarrierInfo: Record<string, CarrierStaticInfo>
  ): void {
    carrierStaticInfo.value = newCarrierInfo;
  }

  function clearOverviewStaticInfo(): void {
    destinationOverviewStaticInfo.value = {};
    subIngredientsOverviewStaticInfo.value = {};
    heartedHotelsOverviewStaticInfo.value = {};
    pendingDestinationKeys.clear();
    pendingSubIngredientKeys.clear();
  }

  return {
    // State
    airportStaticInfo: readonlyRef(airportStaticInfo),
    seaportStaticInfo: readonlyRef(seaportStaticInfo),
    carrierStaticInfo: readonlyRef(carrierStaticInfo),
    carrierNames: readonlyRef(carrierNames),
    destinationOverviewStaticInfo: readonlyRef(destinationOverviewStaticInfo),
    subIngredientsOverviewStaticInfo: readonlyRef(
      subIngredientsOverviewStaticInfo
    ),
    heartedHotelsOverviewStaticInfo: readonlyRef(
      heartedHotelsOverviewStaticInfo
    ),

    // Getters
    getOverviewStaticInfo,
    hasDestinationStaticInfo,
    getSubIngredientStaticInfo,
    hasSubIngredientStaticInfo,
    getPortStaticInfo,
    getPortsStaticInfo,
    getCarriersStaticInfo,
    getPortCodesWithStaticInfo,
    getCarrierCodesWithStaticInfo,

    // Actions
    markDestinationAsPending,
    markSubIngredientAsPending,
    clearPendingDestination,
    clearPendingSubIngredient,
    insertOverviewStaticInfo,
    insertCarrierStaticInfo,
    insertPortStaticInfo,
    insertCarrierNames,
    setCarrierStaticInfo,
    clearOverviewStaticInfo,
  };
});
